import React, { useEffect, useState } from 'react';

import {
    Button, Group, MultiSelect, Space, Stack, Text, TextInput, Title, Skeleton,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useLocalStorage } from '@mantine/hooks';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';

import Fetcher from '../common/communicate';

dayjs.locale('ru');

const DATE_SERVER_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
const DATE_CLIENT_FORMAT = 'D MMMM';

const ALL_STORY_TYPES = [
    { value: 'cybersport', label: 'Киберспорт' },
    { value: 'gof', label: 'Игры Будущего' },
    { value: 'static', label: 'Статичные страницы' },
    { value: 'news', label: 'Новости' },
    { value: 'review', label: 'Рецензии' },
    { value: 'preview', label: 'Превью' },
    { value: 'feat', label: 'Фичер' },
    { value: 'secret', label: 'Секреты' },
    { value: 'hard', label: 'Железо' },
    { value: 'faq', label: 'Карточки' },
    { value: 'video', label: 'Видео' },
    { value: 'file', label: 'Файлы' },
    { value: 'article', label: 'Статья' },
    { value: 'compilation', label: 'Подборка' },
    { value: 'commercial', label: 'Коммерческий материал' },
];

export default function ReportScreen() {
    const [date, setDate] = useState([null, null]);
    const [storyTypes, setStoryTypes] = useState(ALL_STORY_TYPES.map((type) => type.value));
    const [authorSearch, setAuthorSearch] = useState('');
    const [authorOptions, setAuthorOptions] = useState([]);
    const [selectedAuthors, setSelectedAuthors] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [reports, setReports] = useLocalStorage({ key: 'content-reports', defaultValue: [] });

    useEffect(() => {
        if (!authorSearch.trim()) {
            setAuthorOptions([]);
            return undefined;
        }

        Fetcher(`authors/?search=${encodeURIComponent(authorSearch.trim())}`, 'GET', (data) => {
            setAuthorOptions(data.results || []);
        }, null, true);

        return undefined;
    }, [authorSearch]);

    function createReport() {
        if (!date || !date[0] || !date[1]) {
            return;
        }

        setLoading(true);

        const from = dayjs(date[0]).format(DATE_SERVER_FORMAT);
        const to = dayjs(date[1]).add(23, 'h').add(59, 'm').add(59, 's')
            .format(DATE_SERVER_FORMAT);

        Fetcher('v4/story/report/', 'POST', (data) => {
            setReports([
                ...reports,
                {
                    from,
                    to,
                    type: storyTypes.length === 0 || storyTypes.length === ALL_STORY_TYPES.length
                        ? 'Все материалы' : storyTypes
                        .map((type) => ALL_STORY_TYPES.find((item) => item.value === type)?.label)
                        .join(', '),
                    ...data,
                },
            ]);

            setLoading(false);
            setDate([null, null]);
        }, {
            date_from: from,
            date_to: to,
            types: storyTypes,
            authors: selectedAuthors.map((author) => author.email),
            project: 'media.vkplay.ru',
        });
    }

    function addAuthor(author) {
        setSelectedAuthors((current) => (
            current.some((selected) => selected.email === author.email)
                ? current
                : [...current, author]
        ));
        setAuthorSearch('');
        setAuthorOptions([]);
    }

    function removeAuthor(email) {
        setSelectedAuthors((current) => current.filter((author) => author.email !== email));
    }

    function removeReport(e) {
        const { index } = e.currentTarget.dataset;

        setReports(reports.filter((_, i) => i !== +index));
    }

    return (
        <div>
            <Space h="sm" />
            <Title order={1}>Отчёты</Title>
            <Space h="lg" />
            <Group align="flex-end">
                <DatePickerInput
                    type="range"
                    locale="ru"
                    label="Временной промежуток"
                    placeholder="Выбрать"
                    value={date}
                    onChange={setDate}
                    maw={310}
                />
                <MultiSelect
                    label="Тип материала"
                    placeholder="Выбрать"
                    data={ALL_STORY_TYPES}
                    value={storyTypes}
                    onChange={setStoryTypes}
                    searchable
                    clearable
                    maw={320}
                />
                <Button
                    onClick={createReport}
                >
                    Создать отчёт
                </Button>
            </Group>
            <Stack spacing="xs" mt="md" maw={620}>
                <TextInput
                    label="Добавить автора"
                    placeholder="Поиск по email или нику"
                    value={authorSearch}
                    onChange={(event) => setAuthorSearch(event.currentTarget.value)}
                />
                {authorOptions.map((author) => (
                    <Button
                        key={author.email}
                        variant="subtle"
                        size="xs"
                        compact
                        onClick={() => addAuthor(author)}
                    >
                        {author.nick}
                        {' ('}
                        {author.email}
                        {')'}
                    </Button>
                ))}
                {selectedAuthors.length > 0 && (
                    <Group spacing="xs">
                        {selectedAuthors.map((author) => (
                            <Button
                                key={author.email}
                                variant="light"
                                size="xs"
                                compact
                                onClick={() => removeAuthor(author.email)}
                            >
                                {author.nick || author.email}
                                {' ×'}
                            </Button>
                        ))}
                    </Group>
                )}
            </Stack>
            <Space h="xl" />
            <Space h="xl" />
            <Stack>
                {reports.map(({
                    from, to, url, type,
                }, i) => (
                    <Group key={`${from}-${to}`}>
                        <Text style={{ width: 458 }}>
                            {dayjs(from).format(DATE_CLIENT_FORMAT)}
                            {' '}
                            —
                            {dayjs(to).format(DATE_CLIENT_FORMAT)}
                            {' '}
                            (
                            {type || 'Все материалы'}
                            )
                        </Text>
                        <Button
                            variant="outline"
                            component="a"
                            href={url}
                            target="__blank"
                        >
                            Скачать
                        </Button>
                        <Button
                            variant="light"
                            color="red"
                            data-index={i}
                            onClick={removeReport}
                        >
                            Удалить
                        </Button>
                    </Group>
                ))}
                {isLoading && <Skeleton height={36} width={466} />}
            </Stack>
        </div>
    );
}
